import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { waLink } from "../config";
import { Reveal, Page, Eyebrow, MaskedLine, Tilt3D } from "../components/Reveal";
import SEO from "../components/SEO";
import { WhatsAppIcon } from "../components/icons";

const Sobre = () => {
  const { t } = useLang();

  return (
    <Page>
      <SEO title={t.meta.sobre.title} desc={t.meta.sobre.desc} />

      <section className="relative overflow-hidden bg-cream pt-32 pb-20 sm:pt-40" data-testid="about-hero">
        <div aria-hidden="true" className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-blush/50 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="relative order-2 mx-auto w-full max-w-md lg:order-1">
            <div aria-hidden="true" className="absolute -inset-3 arch-frame border border-rose/40" />
            <Tilt3D max={5}>
            <div className="arch-frame img-zoom overflow-hidden shadow-[0_40px_75px_-28px_rgba(168,94,112,0.55)]">
              <img
                src="/assets/monique-gray.jpg"
                alt="Monique Santos — hairstylist da Bloom Beauty Concept Hair"
                className="h-[30rem] w-full object-cover sm:h-[36rem]"
                data-testid="about-monique-photo"
              />
            </div>
            </Tilt3D>
            <p className="mt-5 text-center font-script text-3xl text-rose-gradient">{t.about.quote}</p>
          </div>

          <div className="order-1 lg:order-2">
            <Eyebrow className="mb-5">{t.about.eyebrow}</Eyebrow>
            <h1 className="gold-hairline font-serif text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl" data-testid="about-title">
              <MaskedLine delay={0.05}>{t.about.title}</MaskedLine>
            </h1>
            <p className="mt-10 text-base leading-relaxed text-ink/65 sm:text-lg">{t.about.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-ink/65">{t.about.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-ink/65">{t.about.p3}</p>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="courses-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto mb-12 max-w-3xl text-center">
            <Eyebrow>{t.about.coursesEyebrow}</Eyebrow>
            <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">{t.about.coursesTitle}</h2>
            <p className="mt-8 text-base leading-relaxed text-ink/65 sm:text-lg">{t.about.coursesText}</p>
          </Reveal>
          <div className="grid gap-7 md:grid-cols-2">
            <Reveal>
              <div className="card-lift overflow-hidden rounded-3xl bg-cream ring-1 ring-blush">
                <img src="/assets/curso-tratamentos-capilares.jpeg" alt={t.about.courseHairTreatments} className="h-[30rem] w-full object-contain" loading="lazy" />
                <div className="p-6">
                  <h3 className="font-serif text-2xl font-semibold text-ink">{t.about.courseHairTreatments}</h3>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="card-lift overflow-hidden rounded-3xl bg-cream ring-1 ring-blush">
                <img src="/assets/curso-alisamento.jpeg" alt={t.about.courseStraightening} className="h-[30rem] w-full object-contain" loading="lazy" />
                <div className="p-6">
                  <h3 className="font-serif text-2xl font-semibold text-ink">{t.about.courseStraightening}</h3>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-cream py-20 sm:py-28" data-testid="lorena-section">
        <div aria-hidden="true" className="pointer-events-none absolute -right-32 top-10 h-80 w-80 rounded-full bg-blush/45 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="relative mx-auto w-full max-w-md">
            <div aria-hidden="true" className="absolute -inset-3 arch-frame border border-rose/40" />
            <Tilt3D max={5}>
              <div className="arch-frame img-zoom overflow-hidden shadow-[0_40px_75px_-28px_rgba(168,94,112,0.55)]">
                <img src="/assets/lorena.jpeg" alt="Lorena — profissional da Bloom Beauty Concept Hair" className="h-[30rem] w-full object-cover sm:h-[36rem]" loading="lazy" />
              </div>
            </Tilt3D>
          </div>
          <Reveal>
            <Eyebrow className="mb-5">{t.about.lorenaEyebrow}</Eyebrow>
            <h2 className="gold-hairline font-serif text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl">{t.about.lorenaTitle}</h2>
            <p className="mt-10 text-base leading-relaxed text-ink/65 sm:text-lg">{t.about.lorenaText}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <span className="rounded-full border border-rose/30 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-rosedeep">{t.about.lorenaLaser}</span>
              <span className="rounded-full border border-rose/30 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-rosedeep">{t.about.lorenaSkin}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="philosophy-section">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="font-script text-4xl text-gold">Bloom</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-ink sm:text-4xl">{t.about.philosophyTitle}</h2>
            <p className="mx-auto mt-7 max-w-2xl font-serif text-xl italic leading-relaxed text-ink/70 sm:text-2xl">
              “{t.about.philosophy}”
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-28" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-14">
            <Eyebrow>{t.about.eyebrow}</Eyebrow>
            <h2 className="gold-hairline mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
              {t.about.valuesTitle}
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.about.values.map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="card-lift h-full rounded-3xl bg-white p-7 ring-1 ring-blush">
                  <span className="font-serif text-4xl font-semibold text-blush">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-xl font-semibold text-ink">{v.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/55">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 text-center">
            <a
              href={waLink(t.waDefault)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-whatsapp-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-rosedeep px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_18px_40px_-14px_rgba(168,94,112,0.8)] transition-all duration-300 hover:bg-ink"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t.about.cta}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </Page>
  );
};

export default Sobre;
